import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import Milestone from "../types/milestone";
import JobMilestone from "../types/jobMilestone";

interface MilestoneState {
  milestone: Milestone;
  milestoneList: Milestone[];
  status: "idle" | "loading" | "failed";
}

const initialState: MilestoneState = {
  milestone: {
    id: 0,
    title: "",
  },
  milestoneList: [],
  status: "idle",
};

export const postMilestone = createAsyncThunk<Milestone, Milestone>(
  "milestone/post",
  async (milestone: Milestone) => {
    const response = await http.post<Milestone>(`milestone`, milestone);
    return response.data;
  }
);

export const getMilestoneId = createAsyncThunk<Milestone, number>(
  "milestone/get/id",
  async (id: number) => {
    const response = await http.get<Milestone>(`milestone/${id}`);
    return response.data;
  }
);

export const getMilestones = createAsyncThunk<Milestone[]>(
  "milestone/get/all",
  async () => {
    const response = await http.get<Milestone[]>(`milestone/all`);
    return response.data;
  }
);

export const editMilestone = createAsyncThunk<JobMilestone, JobMilestone>(
  "milestone/edit",
  async (milestone: JobMilestone) => {
    const response = await http.put<JobMilestone>(
      `milestone/${milestone.id}`,
      milestone
    );
    return response.data;
  }
);

export const deleteMilestone = createAsyncThunk<Milestone, number>(
  "milestone/delete",
  async (id: number) => {
    const response = await http.delete<Milestone>(`milestone/${id}`);
    return response.data;
  }
);

const milestoneSlice = createSlice({
  name: "milestone",
  initialState,
  reducers: {
    resetMilestone(state) {
      state.milestone = initialState.milestone;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postMilestone.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postMilestone.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(postMilestone.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getMilestoneId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getMilestoneId.fulfilled, (state, action) => {
        state.status = "idle";
        state.milestone = action.payload;
      })
      .addCase(getMilestoneId.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getMilestones.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getMilestones.fulfilled, (state, action) => {
        state.status = "idle";
        state.milestoneList = action.payload;
      })
      .addCase(getMilestones.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editMilestone.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editMilestone.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(editMilestone.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteMilestone.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteMilestone.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteMilestone.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default milestoneSlice.reducer;
export const { resetMilestone } = milestoneSlice.actions;
